import DoneIcon from '@mui/icons-material/Done';
import InfoIcon from '@mui/icons-material/Info';
import { IconButton, Select, Stack, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tooltip from '@mui/material/Tooltip';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import CustomSnackbar from 'components/CustomSnackbar';
import { formatDateRange } from 'helpers/helpers.js';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import GenericModal from 'components/GenericModal';

const DATE_TO_DATE_LABEL = 'date to date';

const convertToTempOption = option => {
  if (!option) return { label: '', value: '' };
  if (option.label === DATE_TO_DATE_LABEL) {
    return { label: DATE_TO_DATE_LABEL, value: DATE_TO_DATE_LABEL };
  }
  return option;
};

const formatDisplayLabel = option => {
  if (!option) return '';
  if (option.label === DATE_TO_DATE_LABEL) {
    const [start, end] = option.value?.split('_') ?? [];
    if (start && end) {
      const startDate = DateTime.fromFormat(start, 'yyyy-MM-dd');
      const endDate = DateTime.fromFormat(end, 'yyyy-MM-dd');
      if (startDate.isValid && endDate.isValid) {
        return formatDateRange(startDate, endDate);
      }
    }
    return DATE_TO_DATE_LABEL;
  }
  return option.label ?? '';
};

/**
 * A button that opens a dialog containing a select (dropdown)
 * @param {*} props
 * @returns
 */
export default function HeatMapDialogSelect(props) {
  const [open, setOpen] = React.useState(false);
  const [selectedStartDate, setSelectedStartDate] = React.useState(() => DateTime.now().startOf('day'));
  const [selectedEndDate, setSelectedEndDate] = React.useState(() => DateTime.now().startOf('day'));
  const [endDateMaxDate, setEndDateMaxDate] = React.useState(DateTime.now());
  const [otherTimeFrame, setOtherTimeFrame] = React.useState(false);
  const hasMounted = useRef(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [tempSelectedOption, setTempSelectedOption] = React.useState(() => convertToTempOption(props.default));
  const [finalSelectedOption, setFinalSelectedOption] = React.useState(props.default ?? { label: '', value: '' });

  const minDate = DateTime.now().minus({ days: 31 });
  const startDateMaxDate = DateTime.now();
  const timeRangesListRef = useRef(null);
  const dispatch = useDispatch();

  function getMinDate(date1, date2) {
    return date1 < date2 ? date1 : date2;
  }

  /**
   * Set the selected option
   * @param {*} event
   */
  const handleChange = event => {
    let selected = event.target.value;

    let timeRangeobj;
    if (selected !== DATE_TO_DATE_LABEL) {
      setOtherTimeFrame(false);
      timeRangeobj = props.selectOptions.find(option => option.value === Number(selected));
      setTempSelectedOption(timeRangeobj);
    } else {
      setOtherTimeFrame(true);
      setTempSelectedOption({ label: DATE_TO_DATE_LABEL, value: DATE_TO_DATE_LABEL });
    }
  };

  const handleClickOpen = () => {
    setTempSelectedOption(convertToTempOption(finalSelectedOption));
    setOtherTimeFrame(finalSelectedOption?.label === DATE_TO_DATE_LABEL);
    setOpen(true);
  };

  const handleClose = () => {
    setTempSelectedOption(convertToTempOption(finalSelectedOption));
    setOtherTimeFrame(finalSelectedOption?.label === DATE_TO_DATE_LABEL);
    setOpen(false);
  };

  const handleOK = async reason => {
    if (reason === 'backdropClick') {
      return;
    }

    if (tempSelectedOption.label === DATE_TO_DATE_LABEL) {
      if (selectedStartDate === null || selectedEndDate === null) {
        alert('please select date range');
        return;
      }
      const diffInDays = selectedEndDate.diff(selectedStartDate, 'days').days;
      if (diffInDays >= 7) {
        setSnack({
          open: true,
          message: 'Dates must be a maximum of 7 days apart.',
          severity: 'error',
        });
        return;
      }
      const selectedDate = {
        label: DATE_TO_DATE_LABEL,
        value: `${selectedStartDate.toFormat('yyyy-MM-dd')}_${selectedEndDate.toFormat('yyyy-MM-dd')}`,
      };
      dispatch(props.onSelectionOK(selectedDate));
      setFinalSelectedOption(selectedDate);
      setOpen(false);
      return;
    }

    dispatch(props.onSelectionOK(tempSelectedOption));
    setFinalSelectedOption(tempSelectedOption);
    setOpen(false);
  };

  const handleStartDateChange = date => {
    setSelectedStartDate(date);
    if (date && selectedEndDate && date > selectedEndDate) {
      setSelectedEndDate(date);
    }
  };

  const handleEndDateChange = date => {
    setSelectedEndDate(date);
  };

  const getOptions = () => {
    return props.selectOptions?.map(option => {
      return (
        <option value={option.value ?? 0} key={option.value}>
          {option.label ?? ''}
        </option>
      );
    });
  };

  useEffect(() => {
    if (!props.default) return;
    setFinalSelectedOption(props.default);
    if (!open) {
      setTempSelectedOption(convertToTempOption(props.default));
      setOtherTimeFrame(props.default.label === DATE_TO_DATE_LABEL);
    }
  }, [props.default, open]);

  useEffect(() => {
    if (hasMounted.current) {
      const newMaxDate = getMinDate(DateTime.now(), selectedStartDate.plus({ days: 6 }));
      setEndDateMaxDate(newMaxDate);
    } else {
      hasMounted.current = true; // Mark as mounted after the first render
    }
  }, [selectedStartDate]);

  const dialogTitleNode = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span">{props.dialogTitle ?? ''}</Box>
      {props.toolTip && (
        <Tooltip
          title={props.toolTip}
          slotProps={{
            popper: {
              sx: { zIndex: 50020 },
            },
          }}>
          <IconButton size="small" sx={{ color: 'grey', p: 0.5 }}>
            <InfoIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const HeatMapDialogSelectContent = () => (
    <>
      <Box
        component="form"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}>
        <FormControl sx={{ m: 1, minWidth: 120, flex: 1 }}>
          <InputLabel>{props.selectLabel ?? ''}</InputLabel>
          <Select
            ref={timeRangesListRef}
            native
            value={tempSelectedOption.value ?? ''}
            onChange={handleChange}
            input={<OutlinedInput label={props.selectLabel ?? ''} />}>
            {getOptions()}
          </Select>
        </FormControl>
        <IconButton
          aria-label="ok"
          onClick={handleOK}
          sx={{
            mt: 1,
            '&:hover': {
              backgroundColor: 'rgba(225, 225, 225, 1)',
              color: 'black',
            },
          }}>
          <DoneIcon />
        </IconButton>
      </Box>
      {otherTimeFrame && (
        <Stack sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              sx={{
                marginTop: 1,
                width: 265,
              }}
              label="Start date"
              value={selectedStartDate}
              onChange={handleStartDateChange}
              minDate={minDate}
              maxDate={startDateMaxDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd/MM/yyyy"
            />
            <DatePicker
              sx={{
                marginTop: 1,
                width: 265,
              }}
              label="End date"
              value={selectedEndDate}
              onChange={handleEndDateChange}
              minDate={selectedStartDate || minDate}
              maxDate={endDateMaxDate}
              slots={{ textField: TextField }}
              slotProps={{
                textField: { variant: 'outlined' },
                popper: { sx: { zIndex: 50010 } },
              }}
              format="dd/MM/yyyy"
            />
          </LocalizationProvider>
        </Stack>
      )}
    </>
  );

  return (
    <div>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="center"
        color="#fff"
      />
      <MDButton color={'white'} variant="text" onClick={handleClickOpen}>
        <Stack>
          <MDTypography variant="button" fontWeight="bold" color={props.color}>
            {props.buttonTitle ?? ''}
          </MDTypography>
          <MDTypography className="bar-display-selected" variant="caption" fontWeight="light" color={props.color}>
            {formatDisplayLabel(finalSelectedOption)}
          </MDTypography>
        </Stack>
      </MDButton>
      <GenericModal
        open={open}
        onClose={handleClose}
        title={dialogTitleNode}
        Component={HeatMapDialogSelectContent}
        contentProps={{
          dividers: true,
          sx: {
            p: 2,
            borderBottom: 'none !important',
          },
        }}
        closeOnBackdrop={true}
        closeOnEsc={true}
      />
    </div>
  );
}
